/**
 * The most generic type of item.
 * @memberOf things
 */
class Thing
{
    /**
     * Creates a new Thing instance.
     * @constructor
     * @param object The generic object to initialize the object the first time.
     */
    constructor( object = null )
    {
        /**
         * An additional type for the item, typically used for adding more specific types from external vocabularies in microdata syntax.
         * @type {string|Object}
         */
        this.additionalType = null ;

        /**
         * Indicates if the item is active.
         * @type {boolean}
         */
        this.active = true ;

        /**
         * An alias for the item.
         * @type {string}
         */
        this.alternateName = null ;

        /**
         * The creation date of the item.
         * @type {string|Date}
         */
        this.created = null ;

        /**
         * A description of the item.
         * @type {string}
         */
        this.description = null ;

        /**
         * A sub property of description. A short description of the item used to disambiguate from other, similar items. Information from other properties (in particular, name) may be necessary for the description to be useful for disambiguation.
         * @type {string}
         */
        this.disambiguatingDescription = null ;

        /**
         * The index of the item.
         * @type {Object}
         */
        this.id = null ;

        /**
         * The identifier of the item.
         * @type {Object}
         */
        this.identifier = null ;

        /**
         * An image of the item. This can be a URL or a fully described ImageObject.
         * @type {string|Object}
         */
        this.image = null ;

        /**
         * The modification date of the item.
         * @type {string|Date}
         */
        this.modified = null ;

        /**
         * The name of the item.
         * @type {string}
         */
        this.name = null ;

        /**
         * URL of a reference Web page that unambiguously indicates the item's identity.
         * @type {string}
         */
        this.sameAs = null ;

        /**
         * A CreativeWork or Event about this Thing. Inverse property: about.
         * @type {Thing}
         */
        this.subjectOf = null ;

        /**
         * URL of the item.
         * @type {string}
         */
        this.url = null ;

        if( object )
        {
            for (let prop in object)
            {
                this[prop] = object[prop];
            }
            this.populate() ;
        }
    }

    /**
     * Clear the object.
     * @return {Object} The current item reference.
     */
    clear()
    {
        this.active = true ;
        this.additionalType =
        this.alternateName =
        this.created =
        this.description =
        this.disambiguatingDescription =
        this.id =
        this.identifier =
        this.image =
        this.modified =
        this.name =
        this.sameAs =
        this.subjectOf =
        this.url = null ;
        return this ;
    }

    /**
     * Returns a shallow copy of the object.
     * @return {Thing} a shallow copy of the object.
     */
    clone()
    {
        return new Thing( this.toObject() ) ;
    }

    /**
     * A utility function for implementing the <code>toString()</code> method in custom classes. Overriding the <code>toString()</code> method is recommended, but not required.
     * @name formatToString
     * @memberof things
     * @instance
     * @function
     * @param {...string} [rest] - rest The properties of the class and the properties that you add in your custom class.
     * @example
     * class CustomThing extends Thing
     * {
     *     constructor( name )
     *     {
     *         this.name = name;
     *     }
     *
     *     toString()
     *     {
     *         return this.formatToString( "name" );
     *     }
     * }
     */
    formatToString( ...rest )
    {
        if( this._constructorName === null )
        {
            Object.defineProperty( this , '_constructorName' , { value : this.constructor.name } );
        }
        const path = [ this._constructorName ] ;
        const len = rest.length ;
        for( let i = 0; i < len ; ++i )
        {
            if( rest[i] in this )
            {
                path.push( rest[i] + ":" + this[rest[i]] ) ;
            }
        }
        return "[" + path.join(' ') + "]" ;
    }

    /**
     * Returns the locale description of the word item.
     * @return {String} The locale description of the word item.
     */
    getLocaleDescription( lang = 'en' )
    {
        const { description } = this ;
        if( description instanceof String || typeof(description) === 'string' )
        {
            return description ;
        }
        else if ( description && description.hasOwnProperty(lang) )
        {
            return description[lang]
        }
        else
        {
            return null ;
        }
    }

    /**
     * Returns the locale name of the word item.
     * @return {String} The locale name of the word item.
     */
    getLocalName( lang = 'en' )
    {
        const { name } = this ;
        if( name instanceof String || typeof(name) === 'string' )
        {
            return name ;
        }
        else if ( name && name.hasOwnProperty(lang) )
        {
            return name[lang]
        }
        else
        {
            return null ;
        }
    }

    /**
     * Populate the object with specific behaviors or types. Launched by default in the constructor and in the set methods.
     * @returns {Thing} The current reference.
     */
    populate()
    {
        return this ;
    }

    /**
     * Sets the object with a generic object.
     * @return {Object} The current item reference.
     */
    set( object )
    {
        if( object )
        {
            for (let prop in object)
            {
                this[prop] = object[prop];
            }
            this.populate();
        }
        return this ;
    }

    /**
     * Returns the generic object representation of the item.
     * @return {Object} The generic object representation of the item.
     */
    toObject()
    {
        return {
            additionalType : this.additionalType ,
            active : this.active,
            alternateName : this.alternateName,
            created : this.created,
            description : this.description,
            disambiguatingDescription : this.disambiguatingDescription,
            id : this.id,
            identifier : this.identifier,
            image : this.image,
            modified : this.modified,
            name : this.name,
            sameAs : this.sameAs,
            subjectOf : this.subjectOf,
            url : this.url
        };
    }

    toString()
    {
        return this.formatToString() ;
    }

    /**
     * @private
     */
    _constructorName = null ;
}
export default Thing ;


// WEBPACK FOOTER //
// ./src/app/things/Thing.js